"use client";

import { useState } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  PillGroup,
  StatCard,
  StatGrid,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  Explanation,
  Formula,
  ResultTable,
  WorkingPanel,
} from "@/components/tools/stats-descriptive/ui";
import {
  adjustedRows,
  csvText,
  methodName,
  stepRows,
  summaryRows,
  ThresholdPlot,
} from "@/components/tools/stats-descriptive-more/comparison-views";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseIntegerInput, parseNumberInput } from "@/lib/parse-number";
import { formatStat } from "@/lib/tool-kit/stats-descriptive/format";
import {
  ADJUST_METHODS,
  adjustReport,
  parseLabelled,
  parsePList,
} from "@/lib/tool-kit/stats-descriptive-more/multiple-comparisons";

/** English strings. A translated content file overrides these through `ui`. */
export const MULTIPLE_COMPARISONS_CORRECTION_CALCULATOR_STRINGS = {
  entryLabel: "Data entry",
  entryList: "A list of p-values",
  entryLabelled: "Labelled — one “name, p” per line",
  listLabel: "Your p-values — separated by commas, spaces, or new lines",
  listPlaceholder: "0.001, 0.008, 0.039, 0.041, 0.042, 0.06",
  labelledLabel: "Your tests — one “name, p-value” per line",
  labelledPlaceholder: "Reading, 0.049\nWriting, 0.03\nMath, 0.8",
  pDetected: "one: p-value read|other: p-values read",
  invalidHint:
    "one: , {count} entry skipped (not between 0 and 1)|other: , {count} entries skipped (not between 0 and 1)",
  alphaLabel: "Significance level α",
  alphaPlaceholder: "0.05",
  familyLabel: "Total number of tests (optional)",
  familyHint:
    "Only if some tests are not listed. The unlisted p-values are treated as 1, as in R’s p.adjust.",
  tryExample: "Try an example",
  tryLabelled: "Try a labelled example",
  exampleList:
    "0.001, 0.008, 0.039, 0.041, 0.042, 0.06, 0.074, 0.205, 0.212, 0.216",
  exampleLabelled:
    "Reading, 0.049\nWriting, 0.03\nMath, 0.8\nScience, 0.011\nHistory, 0.004\nArt, 0.0003",
  clear: "Clear",
  enterPrompt: "Enter at least one p-value between 0 and 1.",
  errorAlpha: "Enter a significance level α between 0 and 1.",
  errorFamily:
    "The total number of tests must be a whole number, at least as large as the list.",
  results: "Results",
  cardTests: "Tests in the family (m)",
  cardRaw: "Significant before correction",
  cardBonferroni: "Significant, Bonferroni",
  cardHolm: "Significant, Holm",
  cardBh: "Significant, Benjamini–Hochberg",
  cardBy: "Significant, Benjamini–Yekutieli",
  adjustedTitle: "Adjusted p-values",
  colTest: "Test",
  colRaw: "Raw p",
  testLabel: "Test {n}",
  sigMark: "*",
  sigNote:
    "* marks a p-value at or below α = {alpha}. Adjusted p-values are compared with α directly.",
  nameBonferroni: "Bonferroni",
  nameSidak: "Šidák",
  nameHolm: "Holm",
  nameHolmSidak: "Holm–Šidák",
  nameHochberg: "Hochberg",
  nameHommel: "Hommel",
  nameBh: "Benjamini–Hochberg",
  nameBy: "Benjamini–Yekutieli",
  summaryTitle: "The methods compared",
  colMethod: "Method",
  colControls: "Controls",
  colRule: "Adjusted p-value",
  colRejected: "Rejected at α",
  fwer: "Family-wise error rate",
  fdr: "False discovery rate",
  ruleBonferroni: "min(1, m × p)",
  ruleSidak: "1 − (1 − p)^m — exact for independent tests",
  ruleHolm: "Step-down: max over smaller ranks of (m − rank + 1) × p",
  ruleHolmSidak: "Step-down with 1 − (1 − p)^(m − rank + 1)",
  ruleHochberg: "Step-up: min over larger ranks of (m − rank + 1) × p",
  ruleHommel: "Closed testing on all intersections (Hommel, 1988)",
  ruleBh: "Step-up: min over larger ranks of (m ÷ rank) × p",
  ruleBy: "Benjamini–Hochberg × (1 + 1/2 + … + 1/m), any dependence",
  stepsTitle: "Holm and Benjamini–Hochberg step by step",
  colRank: "Rank i",
  colSortedP: "Sorted p",
  colHolmFactor: "Holm factor m − i + 1",
  colHolmProduct: "Factor × p",
  colHolmAdj: "Holm adjusted",
  colBhProduct: "(m ÷ i) × p",
  colBhAdj: "BH adjusted",
  stepsNote:
    "The adjusted value is not always the product shown: Holm takes the largest product so far, and Benjamini–Hochberg takes the smallest product from this rank onward, so adjusted p-values never decrease with rank.",
  plotTitle: "Sorted p-values against the thresholds",
  plotLabel:
    "Plot of {n} sorted p-values against the Bonferroni, Holm and Benjamini–Hochberg thresholds at α = {alpha}.",
  plotP: "p-value",
  plotRank: "Rank (smallest p first)",
  plotPoints: "Your p-values",
  lineBonferroni: "Bonferroni: α ÷ m",
  lineHolm: "Holm: α ÷ (m − i + 1)",
  lineBh: "Benjamini–Hochberg: α × i ÷ m",
  plotNote:
    "A p-value on or below a line passes that method’s threshold. Holm and Hochberg reject the smallest ranks that stay under the Holm line; Benjamini–Hochberg rejects every rank up to the last point on or below its line.",
  workingTitle: "How the numbers were found",
  workM: "m = {m} tests, α = {alpha}",
  workNote:
    "Bonferroni, Šidák, Holm, Hochberg and Hommel control the family-wise error rate — the chance of any false positive. Benjamini–Hochberg and Benjamini–Yekutieli control the false discovery rate — the expected share of discoveries that are false — which is less strict and detects more.",
  copySummary: "Copy as CSV",
  copied: "Copied!",
} as const;

type Entry = "list" | "labelled";

export default function MultipleComparisonsCorrectionCalculatorWidget() {
  const strings = useToolUi(MULTIPLE_COMPARISONS_CORRECTION_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [entry, setEntry] = useState<Entry>("list");
  const [listText, setListText] = useState("");
  const [labelledText, setLabelledText] = useState("");
  const [alphaText, setAlphaText] = useState("0.05");
  const [familyText, setFamilyText] = useState("");

  const parsed =
    entry === "list"
      ? parsePList(listText, parseNumberInput)
      : parseLabelled(labelledText, parseNumberInput);
  const alpha = parseNumberInput(alphaText);
  const family =
    familyText.trim() === "" ? null : parseIntegerInput(familyText);
  const alphaOk = alpha !== null && alpha > 0 && alpha < 1;
  const familyOk =
    familyText.trim() === "" ||
    (family !== null && family >= parsed.entries.length);
  const report =
    alphaOk && familyOk && parsed.entries.length > 0
      ? adjustReport(parsed.entries, alpha, family ?? undefined)
      : null;
  const num = (value: number) => formatStat(fmt, value);
  const view = { strings, fmt, num };
  const typed = (entry === "list" ? listText : labelledText) !== "";

  return (
    <>
      <section className="space-y-4">
        <PillGroup
          label={strings.entryLabel}
          name="mc-entry"
          onChange={setEntry}
          options={[
            { value: "list", label: strings.entryList },
            { value: "labelled", label: strings.entryLabelled },
          ]}
          value={entry}
        />
        <TextAreaField
          hint={
            <>
              {fmt(parsed.entries.length)}
              {tPlural(strings.pDetected, locale, parsed.entries.length)}
              {parsed.invalid > 0
                ? tPlural(strings.invalidHint, locale, parsed.invalid, {
                    count: fmt(parsed.invalid),
                  })
                : null}
            </>
          }
          id="mc-data"
          label={entry === "list" ? strings.listLabel : strings.labelledLabel}
          onChange={entry === "list" ? setListText : setLabelledText}
          placeholder={
            entry === "list"
              ? strings.listPlaceholder
              : strings.labelledPlaceholder
          }
          value={entry === "list" ? listText : labelledText}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="mc-alpha"
            label={strings.alphaLabel}
            numeric
            onChange={setAlphaText}
            placeholder={strings.alphaPlaceholder}
            value={alphaText}
          />
          <TextField
            hint={strings.familyHint}
            id="mc-family"
            label={strings.familyLabel}
            numeric
            onChange={setFamilyText}
            value={familyText}
          />
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_SECONDARY}
            onClick={() => {
              setEntry("list");
              setListText(strings.exampleList);
            }}
            type="button"
          >
            {strings.tryExample}
          </button>
          <button
            className={BUTTON_SECONDARY}
            onClick={() => {
              setEntry("labelled");
              setLabelledText(strings.exampleLabelled);
            }}
            type="button"
          >
            {strings.tryLabelled}
          </button>
          <button
            className={BUTTON_SECONDARY}
            disabled={!typed}
            onClick={() => {
              setListText("");
              setLabelledText("");
            }}
            type="button"
          >
            {strings.clear}
          </button>
        </div>
      </section>

      {alphaOk ? null : <ErrorNotice>{strings.errorAlpha}</ErrorNotice>}
      {familyOk ? null : <ErrorNotice>{strings.errorFamily}</ErrorNotice>}
      {report ? (
        <section className="space-y-6">
          <h2 className={WIDGET_H2}>{strings.results}</h2>
          <div aria-live="polite" className="space-y-4">
            <StatGrid>
              <StatCard label={strings.cardTests} value={fmt(report.m)} />
              <StatCard
                label={strings.cardRaw}
                value={fmt(
                  report.rows.filter((row) => row.p <= report.alpha).length
                )}
              />
              <StatCard
                emphasis
                label={strings.cardBonferroni}
                value={fmt(report.rejected.bonferroni)}
              />
              <StatCard
                emphasis
                label={strings.cardHolm}
                value={fmt(report.rejected.holm)}
              />
              <StatCard
                emphasis
                label={strings.cardBh}
                value={fmt(report.rejected.bh)}
              />
              <StatCard
                label={strings.cardBy}
                value={fmt(report.rejected.by)}
              />
            </StatGrid>
          </div>

          <div className="space-y-2">
            <h3 className={WIDGET_H3}>{strings.adjustedTitle}</h3>
            <ResultTable
              head={[
                strings.colTest,
                strings.colRaw,
                ...ADJUST_METHODS.map((method) => methodName(method, strings)),
              ]}
              rows={adjustedRows(report, view)}
              wrapFirstColumn
            />
            <Explanation>
              {t(strings.sigNote, { alpha: num(report.alpha) })}
            </Explanation>
            <CopyButton
              copiedLabel={strings.copied}
              label={strings.copySummary}
              text={csvText(report, view)}
            />
          </div>

          <div className="space-y-2">
            <h3 className={WIDGET_H3}>{strings.summaryTitle}</h3>
            <ResultTable
              head={[
                strings.colMethod,
                strings.colControls,
                strings.colRule,
                strings.colRejected,
              ]}
              rows={summaryRows(report, view)}
              wrapFirstColumn
            />
          </div>

          <ThresholdPlot report={report} view={view} />

          <div className="space-y-2">
            <h3 className={WIDGET_H3}>{strings.stepsTitle}</h3>
            <ResultTable
              head={[
                strings.colRank,
                strings.colSortedP,
                strings.colHolmFactor,
                strings.colHolmProduct,
                strings.colHolmAdj,
                strings.colBhProduct,
                strings.colBhAdj,
              ]}
              rows={stepRows(report, view)}
            />
            <Explanation>{strings.stepsNote}</Explanation>
          </div>

          <WorkingPanel title={strings.workingTitle}>
            <Formula>
              {t(strings.workM, { m: fmt(report.m), alpha: num(report.alpha) })}
            </Formula>
            <Explanation>{strings.workNote}</Explanation>
          </WorkingPanel>
        </section>
      ) : null}
      {report || !alphaOk || !familyOk ? null : (
        <Notice>{strings.enterPrompt}</Notice>
      )}
    </>
  );
}
